import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSuggestedUsers, createConnection } from "../../api/api";
import { defaultPfpUrl, isAccountVerified } from "../utils";
import { Skeleton } from "../ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";
import { useUserRealtime } from "../../context/UserRealtimeContext";
import { PlusIcon } from "../../widgets/icons";

/**
 * @param {string} name
 * @returns {string}
 */
function initials(name) {
  return (name || "U")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * First active affiliation as "role at org".
 * @param {object|null|undefined} person
 * @returns {string|null}
 */
function affiliationLine(person) {
  const affiliations = person?.affiliations || [];
  const aff =
    affiliations.find((a) => a.status === "active") || affiliations[0] || null;
  if (!aff) return null;
  const role = (aff.role || "").trim();
  const org = (aff.organization?.name || "").trim();
  if (role && org) return `${role} at ${org}`;
  if (role) return role;
  if (org) return org;
  return null;
}

/**
 * One suggested person row.
 * @param {Object} props
 * @param {"rail"|"list"} [props.variant]
 * @param {string} props.to
 * @param {string} props.name
 * @param {string|null} [props.subtitle]
 * @param {string} [props.pfpUrl]
 * @param {string} props.actionLabel
 * @param {() => void} props.onAction
 * @param {boolean} props.isActive
 * @param {boolean} props.busy
 * @param {boolean} [props.actionDisabled]
 * @returns {JSX.Element}
 */
function SuggestedRow({
  variant = "rail",
  to,
  name,
  subtitle,
  pfpUrl,
  actionLabel,
  onAction,
  isActive,
  busy,
  actionDisabled = false,
}) {
  if (variant === "list") {
    return (
      <Card className="border-ui-border shadow-profile-sm transition-colors hover:border-accent">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <Link to={to} className="shrink-0">
                <Avatar className="h-16 w-16 border-2 border-ui-border">
                  <AvatarImage
                    src={pfpUrl || defaultPfpUrl}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = defaultPfpUrl;
                    }}
                  />
                  <AvatarFallback className="text-text-secondary">
                    {initials(name)}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div className="min-w-0">
                <Link
                  to={to}
                  className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
                >
                  {name}
                </Link>
                {subtitle ? (
                  <p className="mt-0.5 truncate text-sm text-text-muted">{subtitle}</p>
                ) : null}
              </div>
            </div>
            {actionDisabled ? null : (
            <Button
              type="button"
              className="shrink-0"
              disabled={busy || isActive}
              variant={isActive ? "outline" : "default"}
              onClick={onAction}
              aria-label={actionLabel}
            >
              {busy ? "Sending…" : actionLabel}
            </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex items-center gap-3 py-1.5">
      <Link to={to} className="shrink-0">
        <img
          src={pfpUrl || defaultPfpUrl}
          alt=""
          className="h-10 w-10 rounded-full object-cover"
          onError={(e) => {
            e.currentTarget.src = defaultPfpUrl;
          }}
        />
      </Link>
      <div className="min-w-0 flex-1">
        <Link to={to} className="hover:underline">
          <div className="truncate text-compact font-emphasis leading-tight text-text-primary">{name}</div>
        </Link>
        {subtitle ? (
          <div className="truncate text-caption text-text-muted">{subtitle}</div>
        ) : null}
      </div>
      {actionDisabled ? null : (
      <button
        type="button"
        onClick={onAction}
        disabled={busy || isActive}
        title={busy ? "Sending…" : actionLabel}
        aria-label={busy ? "Sending…" : actionLabel}
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-compact focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-default",
          isActive
            ? "border-ui-border text-text-muted"
            : "border-ui-border bg-ui-card text-text-primary hover:bg-highlight"
        )}
      >
        {busy ? (
          <span className="font-mono text-meta text-text-muted">…</span>
        ) : isActive ? (
          <span className="font-mono text-meta font-emphasis">✓</span>
        ) : (
          <PlusIcon />
        )}
      </button>
      )}
    </div>
  );
}

/**
 * Suggested connections rail for feed / Network.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @param {boolean} [props.alwaysVisible]
 * @param {string} [props.title]
 * @param {boolean} [props.showProfileLink]
 * @param {number} [props.limit]
 * @param {"rail"|"list"} [props.variant]
 * @returns {JSX.Element|null}
 */
export default function SuggestedConnections({
  userId,
  user,
  alwaysVisible = false,
  title = "Suggested for you",
  showProfileLink = true,
  limit = 6,
  variant = "rail",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectedIds, setConnectedIds] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const { reportConnectionChange } = useUserRealtime();
  const invitesLocked = Boolean(user) && !isAccountVerified(user);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    getSuggestedUsers(userId, limit).then((res) => {
      if (cancelled) return;
      if (res.success && Array.isArray(res.data)) {
        setSuggestions(res.data);
      } else {
        setSuggestions([]);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, limit]);

  const handleConnect = async (targetId) => {
    if (invitesLocked || !userId || connectedIds.has(targetId) || busyId) return;
    setBusyId(targetId);
    const result = await createConnection({
      sender_id: userId,
      receiver_id: targetId,
    });
    setBusyId(null);
    if (result.error || result.success === false) {
      console.error(result.error || result.message || "Failed to connect");
      return;
    }
    setConnectedIds((prev) => new Set(prev).add(targetId));
    reportConnectionChange({
      peerUserId: targetId,
      status: "pending_sent",
      connectionId: result.data?.id || 0,
    });
  };

  if (!userId) return null;

  const rows = suggestions.map((s) => {
    const name =
      s.first_name && s.last_name ? `${s.first_name} ${s.last_name}` : s.username;
    const subtitle = affiliationLine(s) || (variant === "list" ? null : s.location || null);
    const to = s.type === "organization" ? `/organization/${s.id}` : `/profile/${s.id}`;
    const isActive = connectedIds.has(s.id);
    const row = (
      <SuggestedRow
        variant={variant}
        to={to}
        name={name}
        subtitle={subtitle}
        pfpUrl={s.pfp_url}
        isActive={isActive}
        busy={busyId === s.id}
        actionLabel={isActive ? "Requested" : "Connect"}
        actionDisabled={invitesLocked}
        onAction={() => handleConnect(s.id)}
      />
    );
    return variant === "list" ? <li key={s.id}>{row}</li> : <React.Fragment key={s.id}>{row}</React.Fragment>;
  });

  if (variant === "list") {
    return (
      <div className="w-full">
        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="border-ui-border shadow-profile-sm">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-16 w-16 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-9 w-24" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : suggestions.length === 0 ? (
          <p className="py-4 text-center text-sm text-text-muted">No suggestions right now.</p>
        ) : (
          <ul className="list-none space-y-4 p-0">{rows}</ul>
        )}
      </div>
    );
  }

  return (
    <aside
      className={
        alwaysVisible
          ? "block w-full"
          : "sticky top-6 hidden w-full self-start lg:block"
      }
    >
      <div className="rounded-card border border-ui-border bg-ui-card p-5 shadow-shell-sm">
          {title || showProfileLink ? (
            <div className="mb-3.5 flex items-center justify-between gap-4">
              {title ? (
                <h2 className="font-display text-title font-medium text-text-primary">{title}</h2>
              ) : (
                <span />
              )}
            </div>
          ) : null}

          {loading ? (
            <div className="space-y-3 py-1">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : suggestions.length === 0 ? (
            <p className="py-4 text-center text-caption text-text-muted">No suggestions right now.</p>
          ) : (
            <div className="space-y-3">{rows}</div>
          )}
      </div>
    </aside>
  );
}
